
import React from 'react';
import { Model, surveyLocalization } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/survey-core.min.css';
import 'survey-core/i18n/traditional-chinese';
import { DefaultDark, DefaultDarkPanelless } from "survey-core/themes";
import { db, auth } from '../lib/firebase';
import { signInAnonymously } from 'firebase/auth';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const STORAGE_KEY = "monospaceMeetingProfile";

// Localization 設定
surveyLocalization.supportedLocales = ["zh-tw", "en"];
surveyLocalization.defaultLocale = "zh-tw";

// Helper function to generate all 30-minute time slots
const generateTimeSlots = (): string[] => {
    const slots: string[] = [];
    for (let hour = 8; hour <= 22; hour++) {
        slots.push(`${hour.toString().padStart(2, '0')}:00`);
        if (hour < 22) {
            slots.push(`${hour.toString().padStart(2, '0')}:30`);
        }
    }
    return slots;
};

// Helper function to get occupied slots from a reservation
const getOccupiedSlots = (start: Date, end: Date): string[] => {
    const slots: string[] = [];
    const current = new Date(start);

    while (current < end) {
        const hours = current.getHours().toString().padStart(2, '0');
        const minutes = current.getMinutes().toString().padStart(2, '0');
        slots.push(`${hours}:${minutes}`);
        current.setMinutes(current.getMinutes() + 30);
    }

    return slots;
};

// Fetch reserved slots and buffer slots for a given date via Cloud Function
const fetchReservedAndBufferSlots = async (date: string): Promise<{ reserved: string[], buffer: string[] }> => {
    try {
        const functions = getFunctions();
        const getAvailableSlots = httpsCallable(functions, 'getAvailableSlots');

        const result = await getAvailableSlots({ date, roomId: '1602B' });
        const data = result.data as { reservations: Array<{ start: string, end: string }> };

        const reservedSlots: string[] = [];
        const bufferSlots: string[] = [];

        data.reservations.forEach(reservation => {
            const start = new Date(reservation.start);
            const end = new Date(reservation.end);

            // Get directly occupied slots (the actual reservation time)
            const occupied = getOccupiedSlots(start, end);
            reservedSlots.push(...occupied);

            // Get buffer slots - 30 min BEFORE reservation start
            const bufferStart = new Date(start.getTime() - 30 * 60 * 1000);
            let current = new Date(bufferStart);
            while (current < start) {
                const hours = current.getHours().toString().padStart(2, '0');
                const minutes = current.getMinutes().toString().padStart(2, '0');
                const slot = `${hours}:${minutes}`;
                bufferSlots.push(slot);
                current.setMinutes(current.getMinutes() + 30);
            }

            // Get buffer slots - 30 min AFTER reservation end
            const bufferEnd = new Date(end.getTime() + 30 * 60 * 1000);
            current = new Date(end);
            while (current < bufferEnd) {
                const hours = current.getHours().toString().padStart(2, '0');
                const minutes = current.getMinutes().toString().padStart(2, '0');
                const slot = `${hours}:${minutes}`;
                bufferSlots.push(slot);
                current.setMinutes(current.getMinutes() + 30);
            }
        });

        // Return unique slots - overlapping buffers are automatically deduplicated
        return {
            reserved: Array.from(new Set(reservedSlots)),
            buffer: Array.from(new Set(bufferSlots))
        };
    } catch (error) {
        console.error("Error fetching reserved and buffer slots:", error);
        return { reserved: [], buffer: [] };
    }
};

// Fetch full reservation objects for conflict checking via Cloud Function
const fetchReservations = async (date: string): Promise<Array<{ start: Date, end: Date }>> => {
    try {
        const functions = getFunctions();
        const getAvailableSlots = httpsCallable(functions, 'getAvailableSlots');

        const result = await getAvailableSlots({ date, roomId: '1602B' });
        const data = result.data as { reservations: Array<{ start: string, end: string }> };

        return data.reservations.map(r => ({
            start: new Date(r.start),
            end: new Date(r.end)
        }));
    } catch (error) {
        console.error("Error fetching reservations:", error);
        return [];
    }
};

// Check if proposed time conflicts with existing reservations (with 30min buffer before and after)
const checkConflictWithBuffer = (
    proposedStart: Date,
    proposedEnd: Date,
    existingReservations: Array<{ start: Date, end: Date }>
): boolean => {
    for (const reservation of existingReservations) {
        // Existing reservation occupies: [start - 30min, end + 30min]
        const resBufferStart = new Date(reservation.start.getTime() - 30 * 60 * 1000);
        const resBufferEnd = new Date(reservation.end.getTime() + 30 * 60 * 1000);

        // Check for overlap with existing reservation + its buffers
        if (proposedStart < resBufferEnd && proposedEnd > resBufferStart) {
            return true; // Conflict found
        }
    }
    return false; // No conflict
};


const surveyJson = {
    locale: "zh-tw",
    title: "會議室預約",
    description:
        "此表單僅供 Monospace 會員預約台中 1602B 會議室使用，實際預約仍以工作人員確認為準。",
    showQuestionNumbers: "off",
    pages: [
        {
            name: "bookingInfo",
            title: "預約資訊",
            elements: [
                {
                    type: "html",
                    name: "roomInfo",
                    html:
                        "<div style='padding:10px 0; font-size:15px;'>" +
                        "<strong>預約會議室：</strong>台中 1602B（建議 6–8 人以下）<br>" +
                        "<strong>可預約時段：</strong>08:00–22:00<br>" +
                        "<strong>單次最多：</strong>4 小時</div>"
                },
                {
                    type: "text",
                    name: "date",
                    title: "預約日期",
                    isRequired: true,
                    inputType: "date"
                },
                {
                    type: "dropdown",
                    name: "startTime",
                    title: "開始時間（08:00–22:00）",
                    isRequired: true,
                    choices: [
                        "08:00", "08:30", "09:00", "09:30", "10:00", "10:30",
                        "11:00", "11:30", "12:00", "12:30", "13:00", "13:30",
                        "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
                        "17:00", "17:30", "18:00", "18:30", "19:00", "19:30",
                        "20:00", "20:30", "21:00", "21:30", "22:00"
                    ]
                },
                {
                    type: "dropdown",
                    name: "durationHours",
                    title: "使用時數",
                    isRequired: true,
                    choices: [
                        { value: 1, text: "1 小時" },
                        { value: 2, text: "2 小時" },
                        { value: 3, text: "3 小時" },
                        { value: 4, text: "4 小時（上限）" }
                    ]
                },
                {
                    type: "dropdown",
                    name: "attendeeCount",
                    title: "預計參與人數",
                    isRequired: true,
                    choices: [
                        { value: "1-3", text: "1~3 人" },
                        { value: "4-6", text: "4~6 人" },
                        { value: "7plus", text: "7 人以上（可能較壅擠）" }
                    ]
                },
                {
                    type: "radiogroup",
                    name: "purpose",
                    title: "會議用途",
                    isRequired: true,
                    hasOther: true,
                    otherText: "其他（請填寫）",
                    otherPlaceholder: "請輸入會議用途",
                    choices: [
                        "與客戶簡報 / 商務洽談",
                        "內部討論 / 專案會議",
                        "視訊 / 線上會議",
                        "面試 / 招募",
                        "教學 / 工作坊"
                    ]
                },
                {
                    type: "checkbox",
                    name: "equipment",
                    title: "設備需求（可複選）",
                    description: "可勾選需要協助準備的設備項目，如有其他需求可自行填寫。",
                    hasOther: true,
                    otherText: "其他（請填寫）",
                    otherPlaceholder: "請描述其他設備需求",
                    choices: [
                        "電視（HDMI 投影）",
                        "白板",
                        "Apple TV",
                        "簡報筆"
                    ]
                }
            ]
        },
        {
            name: "memberInfo",
            title: "會員資料",
            elements: [
                {
                    type: "text",
                    name: "memberName",
                    title: "姓名",
                    isRequired: true
                },
                {
                    type: "text",
                    name: "email",
                    title: "Email",
                    inputType: "email",
                    isRequired: true
                },
                {
                    type: "text",
                    name: "phone",
                    title: "手機號碼（必填）",
                    inputType: "tel",
                    isRequired: true
                },
                {
                    type: "boolean",
                    name: "saveProfile",
                    title: "在這台裝置記住我的姓名 / Email / 手機號，下次自動帶入",
                    labelTrue: "是",
                    labelFalse: "否",
                    defaultValue: true
                }
            ]
        },
        {
            name: "terms",
            title: "租借使用規範",
            elements: [
                {
                    type: "html",
                    name: "termsText",
                    html: `
  <div style="font-size: 14px; line-height: 1.6;">
    <p>為維護 Monospace 空間品質與所有使用者權益，預約會議室即表示您已閱讀並同意以下使用規範：</p>

    <h5 style="font-size: 16px; font-weight: 600; margin-top: 16px; margin-bottom: 8px;">一、預約與取消</h5>
    <ul>
      <li>會議室採預約制，完成送出表單後需等待 Monospace 工作人員確認方為正式預約。</li>
      <li>若需取消或更改時間，請於預約開始 <strong>至少 1 小時前</strong>提出，否則視同使用一次。</li>
      <li>若於高峰時段（如平日白天）連續占用超過可預約上限，Monospace 得視情況調整預約時段。</li>
    </ul>

    <h5 style="font-size: 16px; font-weight: 600; margin-top: 16px; margin-bottom: 8px;">二、使用時間與遲到</h5>
    <ul>
      <li>請依預約時間準時使用及離場，以免影響其他使用者。</li>
      <li>若遲到超過 <strong>15 分鐘未主動告知</strong>，預約時段可能會被釋出提供給其他人使用。</li>
      <li>使用結束後請立即清理並恢復場地原狀，如有設備移動請協助歸位。</li>
    </ul>

    <h5 style="font-size: 16px; font-weight: 600; margin-top: 16px; margin-bottom: 8px;">三、設備使用</h5>
    <ul>
      <li>會議室提供：電視（HDMI 投影）、白板、Apple TV、簡報筆（依空間配置可能略有不同）。</li>
      <li>請勿擅自拆卸、搬離或變更設備架設方式，如有需要請洽工作人員協助。</li>
      <li>若設備於使用期間受損，將依原價或維修費用向預約人收取。</li>
    </ul>

    <h5 style="font-size: 16px; font-weight: 600; margin-top: 16px; margin-bottom: 8px;">四、空間行為規範</h5>
    <ul>
      <li>請保持會議音量於合理範圍內，避免影響其他使用者。</li>
      <li>禁止於會議室內做可能影響安全之行為，包括但不限於：明火、危險物品、強烈氣味物質等。</li>
      <li>若需飲食，請注意避免氣味過重或產生大量垃圾，並務必自行清潔。</li>
      <li>請自行保管個人物品，空間恕不負保管責任。</li>
    </ul>

    <h5 style="font-size: 16px; font-weight: 600; margin-top: 16px; margin-bottom: 8px;">五、場地清潔與恢復</h5>
    <ul>
      <li>使用結束後請自行整理環境、將椅子推回、白板擦拭乾淨並帶走個人物品。</li>
      <li>若使用後空間明顯凌亂、造成額外清潔負擔，Monospace 有權酌收清潔費用。</li>
    </ul>

    <h5 style="font-size: 16px; font-weight: 600; margin-top: 16px; margin-bottom: 8px;">六、責任歸屬</h5>
    <ul>
      <li>會議內容與使用者行為須自行負責，不得違反法律、公共秩序或善良風俗。</li>
      <li>若因使用者行為導致空間、設備或其他會員權益受損，使用者應負賠償責任。</li>
      <li>若使用者活動涉及商業、招生、拍攝等特殊用途，請事先向 Monospace 通知。</li>
    </ul>

    <h5 style="font-size: 16px; font-weight: 600; margin-top: 16px; margin-bottom: 8px;">七、Monospace 權利</h5>
    <ul>
      <li>Monospace 得因維運、突發狀況或不可抗力因素調整或取消預約，並會及時通知使用者。</li>
      <li>對於違反使用規範者，Monospace 得拒絕其未來預約或停止相關服務。</li>
      <li>若使用者活動涉及商業、招生、拍攝等特殊用途，請事先向 Monospace 通知。</li>
    </ul>

    <p><strong>送出預約代表您已閱讀並完全同意以上所有規範內容。</strong></p>
  </div>
    `
                },
                {
                    type: "checkbox",
                    name: "termsConfirm",
                    title: "同意條款",
                    isRequired: true,
                    choices: [
                        "我已閱讀並同意 Monospace 會議室租借使用規範之內容"
                    ]
                }
            ]
        }
    ]
};

const MeetingRoomSurvey: React.FC = () => {
    const survey = React.useMemo(() => {
        const m = new Model(surveyJson);
        m.locale = "zh-tw";
        m.applyTheme(DefaultDarkPanelless);

        // Load profile from localStorage if available
        try {
            const savedProfile = localStorage.getItem(STORAGE_KEY);
            if (savedProfile) {
                const saved = JSON.parse(savedProfile);
                if (saved) {
                    if (saved.memberName) m.setValue("memberName", saved.memberName);
                    if (saved.email) m.setValue("email", saved.email);
                    if (saved.phone) m.setValue("phone", saved.phone);
                    m.setValue("saveProfile", true);
                }
            }
        } catch (e) {
            console.warn("讀取本機會員資料失敗：", e);
        }

        return m;
    }, []);

    const saveProfileIfNeeded = React.useCallback((sender: Model) => {
        const wantSave = !!sender.getValue("saveProfile");
        if (!wantSave) {
            window.localStorage.removeItem(STORAGE_KEY);
            return;
        }
        const profile = {
            memberName: sender.getValue("memberName") || "",
            email: sender.getValue("email") || "",
            phone: sender.getValue("phone") || ""
        };
        if (!profile.memberName && !profile.email && !profile.phone) {
            return;
        }
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
        } catch (e) {
            console.warn("儲存本機會員資料失敗：", e);
        }
    }, []);

    React.useEffect(() => {
        const onCompleteHandler = async (sender: Model) => {
            const data = sender.data;
            console.log("Survey result:", data);
            saveProfileIfNeeded(sender);

            try {
                // Ensure user is signed in
                if (!auth.currentUser) {
                    await signInAnonymously(auth);
                }

                const userId = auth.currentUser?.uid;
                if (!userId) throw new Error("User not authenticated");

                // Calculate start and end times
                const dateStr = data.date; // YYYY-MM-DD
                const timeStr = data.startTime; // HH:mm
                const duration = parseInt(data.durationHours || "1", 10);

                const start = new Date(`${dateStr}T${timeStr}:00`);
                const end = new Date(start.getTime() + duration * 60 * 60 * 1000);

                // Construct reservation object
                const reservation = {
                    userId,
                    roomId: "1602B", // Hardcoded for this survey
                    start,
                    end,
                    purpose: data.purpose,
                    attendeeCount: data.attendeeCount,
                    equipment: data.equipment || [],
                    contact: {
                        name: data.memberName,
                        email: data.email,
                        phone: data.phone
                    },
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                };

                // Save to Firestore
                await addDoc(collection(db, "rooms", "1602B", "reservations"), reservation);

                alert("預約已送出並儲存！");
            } catch (e) {
                console.error("Error saving reservation:", e);
                alert("儲存失敗，請稍後再試或聯繫工作人員。");
            }
        };

        const onValueChangedHandler = async (sender: Model, options: any) => {
            const watchedFields = ["memberName", "email", "phone", "saveProfile"];
            if (watchedFields.includes(options.name)) {
                saveProfileIfNeeded(sender);
            }

            // Handle date selection to update available time slots
            if (options.name === "date") {
                const selectedDate = sender.getValue("date");
                if (selectedDate) {
                    const { reserved, buffer } = await fetchReservedAndBufferSlots(selectedDate);
                    const allSlots = generateTimeSlots();

                    // Get the startTime question
                    const startTimeQuestion = sender.getQuestionByName("startTime");
                    if (startTimeQuestion) {
                        // Create choices with disabled status for reserved and buffer slots
                        const choices = allSlots.map(slot => {
                            const isReserved = reserved.includes(slot);
                            const isBuffer = buffer.includes(slot);

                            if (isReserved) {
                                return {
                                    value: slot,
                                    text: `${slot} (已預約)`,
                                    enableIf: "false"
                                };
                            } else if (isBuffer) {
                                return {
                                    value: slot,
                                    text: `${slot} (已保留)`,
                                    enableIf: "false"
                                };
                            } else {
                                return {
                                    value: slot,
                                    text: slot
                                };
                            }
                        });

                        startTimeQuestion.choices = choices;
                    }
                }
            }

            // Handle startTime or durationHours change to check for conflicts
            if (options.name === "startTime" || options.name === "durationHours") {
                const date = sender.getValue("date");
                const startTime = sender.getValue("startTime");
                const duration = sender.getValue("durationHours");

                // Clear any previous errors first
                const durationQuestion = sender.getQuestionByName("durationHours");
                const startTimeQuestion = sender.getQuestionByName("startTime");
                if (durationQuestion) durationQuestion.clearErrors();
                if (startTimeQuestion) startTimeQuestion.clearErrors();

                // Only validate if all three values are present
                if (date && startTime && duration) {
                    const proposedStart = new Date(`${date}T${startTime}:00`);
                    const proposedEnd = new Date(proposedStart.getTime() + duration * 60 * 60 * 1000);

                    const reservations = await fetchReservations(date);
                    const hasConflict = checkConflictWithBuffer(proposedStart, proposedEnd, reservations);

                    if (hasConflict) {
                        // Set error on duration question
                        if (durationQuestion) {
                            durationQuestion.addError("此時段與現有預約衝突（含緩衝時間30分鐘），請選擇其他時段或時數");
                        }
                    }
                }
            }
        };

        survey.onComplete.add(onCompleteHandler);
        survey.onValueChanged.add(onValueChangedHandler);

        return () => {
            survey.onComplete.remove(onCompleteHandler);
            survey.onValueChanged.remove(onValueChangedHandler);
        };
    }, [survey, saveProfileIfNeeded]);

    // Anonymous Auth on mount
    React.useEffect(() => {
        signInAnonymously(auth).catch((e) => {
            console.error("Anonymous auth failed:", e);
        });
    }, []);

    return (
        <div>
            <Survey model={survey} />
        </div>
    );
};

export default MeetingRoomSurvey;
